import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Plus, Trash } from "lucide-react";
import { fmtMoney } from "@/lib/api";

const TAX_OPTIONS = [
  { v: "0", l: "Không VAT (0%)" },
  { v: "5", l: "VAT 5%" },
  { v: "8", l: "VAT 8%" },
  { v: "10", l: "VAT 10%" },
];

const PAY_METHODS = [
  { v: "cash", l: "Tiền mặt" },
  { v: "card", l: "Thẻ" },
  { v: "transfer", l: "Chuyển khoản" },
  { v: "credit", l: "Công nợ" },
];

export function PaymentDialog({ open, onOpenChange, channel, subtotal, onConfirm, submitting }) {
  const [discount, setDiscount] = useState("0");
  const [taxRate, setTaxRate] = useState("0");
  const [serviceRate, setServiceRate] = useState(channel === "restaurant" ? "5" : "0");
  const [split, setSplit] = useState(false);
  const [singleMethod, setSingleMethod] = useState(channel === "b2b" ? "credit" : "cash");
  const [parts, setParts] = useState([{ method: "cash", amount: "" }]);

  useEffect(() => {
    if (!open) return;
    setDiscount("0");
    setTaxRate("0");
    setServiceRate(channel === "restaurant" ? "5" : "0");
    setSplit(false);
    setSingleMethod(channel === "b2b" ? "credit" : "cash");
    setParts([{ method: "cash", amount: "" }]);
  }, [open, channel]);

  const { taxableBase, serviceCharge, taxAmount, total } = useMemo(() => {
    const d = Number(discount) || 0;
    const sr = Number(serviceRate) || 0;
    const tr = Number(taxRate) || 0;
    const tb = Math.max(0, subtotal - d);
    const sc = Math.round(tb * sr) / 100;
    const tx = Math.round((tb + sc) * tr) / 100;
    return { taxableBase: tb, serviceCharge: sc, taxAmount: tx, total: tb + sc + tx };
  }, [discount, serviceRate, taxRate, subtotal]);

  const partsSum = parts.reduce((a, p) => a + (Number(p.amount) || 0), 0);
  const partsOk = split ? Math.abs(partsSum - total) < 0.01 && parts.every((p) => p.method && Number(p.amount) > 0) : true;

  const confirm = () => {
    const base = {
      discount: Number(discount) || 0,
      tax_rate: Number(taxRate) || 0,
      service_rate: Number(serviceRate) || 0,
      paid_amount: total,   // #68: backend requires paid_amount when payment_method is set
    };
    if (split) {
      onConfirm({ ...base, payments: parts.map((p) => ({ method: p.method, amount: Number(p.amount) })) });
    } else {
      onConfirm({ ...base, payment_method: singleMethod });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm max-w-2xl" data-testid="pos-payment-dialog">
        <DialogHeader>
          <DialogTitle>Thanh toán</DialogTitle>
          <DialogDescription>Nhập chiết khấu, VAT, phí dịch vụ và phương thức thanh toán.</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Chiết khấu (₫)">
            <Input type="number" min="0" value={discount} onChange={(e) => setDiscount(e.target.value)} className="h-9 rounded-sm" data-testid="pay-discount" />
          </Field>
          <Field label="VAT">
            <Select value={taxRate} onValueChange={setTaxRate}>
              <SelectTrigger className="h-9 rounded-sm" aria-label="VAT"><SelectValue /></SelectTrigger>
              <SelectContent>{TAX_OPTIONS.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          {channel === "restaurant" && (
            <Field label="Phí dịch vụ (%)">
              <Input type="number" min="0" max="100" value={serviceRate} onChange={(e) => setServiceRate(e.target.value)} className="h-9 rounded-sm" data-testid="pay-service-rate" />
            </Field>
          )}
        </div>

        <div className="text-xs font-mono space-y-0.5 bg-stone-50 rounded-sm p-3">
          <Row label="Tạm tính" value={subtotal} />
          <Row label="Chiết khấu" value={-Number(discount || 0)} />
          {serviceCharge > 0 && <Row label="Phí dịch vụ" value={serviceCharge} />}
          {taxAmount > 0 && <Row label="VAT" value={taxAmount} />}
          <Row label="Tổng cộng" value={total} bold />
        </div>

        <div className="flex items-center gap-2">
          <input id="pay-split-toggle" type="checkbox" checked={split} onChange={(e) => setSplit(e.target.checked)} />
          <label htmlFor="pay-split-toggle" className="text-sm">Chia thanh toán (nhiều phương thức)</label>
        </div>

        {!split ? (
          <Field label="Phương thức">
            <Select value={singleMethod} onValueChange={setSingleMethod}>
              <SelectTrigger className="h-9 rounded-sm" aria-label="Phương thức"><SelectValue /></SelectTrigger>
              <SelectContent>{PAY_METHODS.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
        ) : (
          <div className="space-y-2">
            {parts.map((p, i) => (
              <div key={i} className="flex gap-2 items-center" data-testid={`pay-split-row-${i}`}>
                <Select value={p.method} onValueChange={(v) => setParts((ps) => ps.map((x, j) => j === i ? { ...x, method: v } : x))}>
                  <SelectTrigger className="h-9 rounded-sm w-40" aria-label={`Phương thức dòng ${i + 1}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{PAY_METHODS.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
                </Select>
                <Input
                  type="number"
                  min="0"
                  placeholder="Số tiền"
                  value={p.amount}
                  onChange={(e) => setParts((ps) => ps.map((x, j) => j === i ? { ...x, amount: e.target.value } : x))}
                  className="h-9 rounded-sm flex-1"
                />
                {parts.length > 1 && (
                  <button type="button" onClick={() => setParts((ps) => ps.filter((_, j) => j !== i))} aria-label="Xóa dòng thanh toán" className="text-stone-400 hover:text-red-600 p-1">
                    <Trash className="w-4 h-4" aria-hidden="true" />
                  </button>
                )}
              </div>
            ))}
            <Button type="button" variant="outline" size="sm" onClick={() => setParts((ps) => [...ps, { method: "card", amount: "" }])} className="rounded-sm">
              <Plus className="w-3 h-3 mr-1" aria-hidden="true" />Thêm phương thức
            </Button>
            <div className={`text-xs font-mono ${partsOk ? "text-emerald-800" : "text-red-700"}`}>
              Đã chia: {fmtMoney(partsSum)} / {fmtMoney(total)}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm" disabled={submitting}>Hủy</Button>
          <Button
            type="button"
            onClick={confirm}
            disabled={submitting || !partsOk || total < 0}
            className="rounded-sm bg-emerald-800 hover:bg-emerald-900"
            data-testid="btn-confirm-payment"
          >
            {submitting ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" aria-hidden="true" />Đang xử lý...</> : `Thanh toán ${fmtMoney(total)}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-xs text-stone-700 mb-1">{label}</div>
      {children}
    </div>
  );
}

function Row({ label, value, bold }) {
  return (
    <div className={`flex justify-between ${bold ? "font-bold text-sm" : ""}`}>
      <span>{label}</span>
      <span>{fmtMoney(value)}</span>
    </div>
  );
}
